/**
 * SmartApplyApplicationState Cache Management Utilities
 *
 * CRITICAL ARCHITECTURAL CONSTRAINT:
 * smartApplyApplicationState mutations do NOT support partial updates!
 *
 * ❌ PROBLEM: Partial mutations clear all fields not included in the mutation
 * ✅ SOLUTION: Always query complete data and mutate with ALL fields (existing + updates)
 *
 * These utilities ensure we never lose data by working with complete state objects.
 */

import { ApolloCache } from '@apollo/client';
import {
    ContactInfoSmartApplyApplicationState,
    ScreenerQuestionsAndAnswersSmartApplyApplicationState,
    ResumeSmartApplyApplicationState,
    SmartApplyApplicationState,
    UpdateContactInfoSmartApplyApplicationStateInput,
    UpdateQuestionsAndAnswersSmartApplyApplicationStateInput,
} from '../../__generated__/graphql-base-types';

// Type-safe update interfaces for partial field updates
export type SmartApplyContactInfoUpdate = Partial<ContactInfoSmartApplyApplicationState>;
export type SmartApplyResumeUpdate = Partial<ResumeSmartApplyApplicationState>;
export type SmartApplyQuestionsUpdate =
    Partial<ScreenerQuestionsAndAnswersSmartApplyApplicationState>;

/**
 * ⚠️  ARCHITECTURAL CONSTRAINT DOCUMENTATION ⚠️
 *
 * WHY WE NEED COMPLETE DATA FOR MUTATIONS:
 *
 * The smartApplyApplicationState API does NOT support partial updates.
 * When you send a mutation with only some fields, the server:
 * 1. ❌ Clears ALL other fields not included in the mutation
 * 2. ❌ Results in permanent data loss
 * 3. ❌ No warning or error - just silent data deletion
 *
 * SOLUTION: Always send complete field objects in mutations:
 * ✅ Query: Fetch ALL smartApplyApplicationState fields within the section being updated
 * ✅ Mutate: Send ALL existing fields + your updates for that specific section
 * ✅ Result: No data loss, predictable behavior
 *
 * IMPORTANT: contactInfo, resume, and questionsAndAnswers can be updated INDEPENDENTLY.
 * You only need complete data within the specific section you're updating:
 * - Updating contactInfo? → Query complete contactInfo, mutate with complete contactInfo
 * - Updating resume? → Query complete resume, mutate with complete resume
 * - Updating questionsAndAnswers? → Query complete questionsAndAnswers, mutate with complete questionsAndAnswers
 */

/**
 * ✅ SAFE: Update smartApplyApplicationState contactInfo with complete data preservation
 *
 * This function only updates the Apollo cache - it does NOT call server mutations.
 * Use this for immediate UI updates, then call buildCompleteContactInfoMutation()
 * when you need to save to the server.
 *
 * @param cache - Apollo cache instance
 * @param id - SmartApplyApplicationState ID
 * @param contactInfoUpdates - Partial contact info updates
 * @returns Updated contactInfo or null if operation failed
 */
export function updateSmartApplyContactInfoCache(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: ApolloCache<any>,
    id: string,
    contactInfoUpdates: SmartApplyContactInfoUpdate,
): ContactInfoSmartApplyApplicationState | null {
    try {
        // Use cache.identify() for proper cache key generation
        const cacheId = cache.identify({
            __typename: 'SmartApplyApplicationState',
            id,
        });

        if (!cacheId) {
            console.warn(`⚠️  Failed to identify SmartApplyApplicationState with id: ${id}`);
            return null;
        }

        let updatedContactInfo: ContactInfoSmartApplyApplicationState | null = null;

        const modifyResult = cache.modify({
            id: cacheId,
            fields: {
                /**
                 * ✅ Type-safe field modifier for contactInfo
                 *
                 * IMPORTANT: This preserves ALL existing fields and only updates the specified ones.
                 * This is critical because server mutations require complete field objects.
                 *
                 * @param existingContactInfo - Current contactInfo from cache (Apollo cache format)
                 * @returns Updated contactInfo (complete object with updates applied)
                 */
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                contactInfo: (existingContactInfo: any) => {
                    // Ensure we have a complete base object (never send partial to server)
                    const baseContactInfo: ContactInfoSmartApplyApplicationState =
                        existingContactInfo || {
                            __typename: 'ContactInfoSmartApplyApplicationState',
                            firstName: null,
                            lastName: null,
                            firstNamePronunciation: null,
                            lastNamePronunciation: null,
                            email: null,
                            phoneNumber: null,
                            dateOfBirth: null,
                            gender: null,
                            area: null,
                            city: null,
                            country: null,
                            resumeContactInfo: null,
                        };

                    // ✅ Merge updates into complete object (all fields preserved)
                    const newContactInfo: ContactInfoSmartApplyApplicationState = {
                        ...baseContactInfo,
                        ...contactInfoUpdates, // Only override specified fields
                    };

                    updatedContactInfo = newContactInfo;
                    return newContactInfo;
                },
            },
        });

        if (!modifyResult) {
            console.warn(
                `⚠️  Cache modify returned false for ${id} - entity may not exist in cache`,
            );
            return null;
        }

        console.log(
            `✅ Updated SmartApplyApplicationState(${id}) contactInfo in cache:`,
            contactInfoUpdates,
        );
        return updatedContactInfo;
    } catch (error) {
        console.error('❌ Failed to update SmartApplyApplicationState cache:', error);
        return null;
    }
}

/**
 * ✅ SAFE: Update smartApplyApplicationState questionsAndAnswers with complete data preservation
 *
 * This function only updates the Apollo cache - it does NOT call server mutations.
 * Use this for immediate UI updates, then call buildCompleteQuestionsAndAnswersMutation()
 * when you need to save to the server.
 *
 * @param cache - Apollo cache instance
 * @param id - SmartApplyApplicationState ID
 * @param questionsUpdates - Partial questions updates
 * @returns Updated questionsAndAnswers or null if operation failed
 */
export function updateSmartApplyQuestionsCache(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: ApolloCache<any>,
    id: string,
    questionsUpdates: SmartApplyQuestionsUpdate,
): ScreenerQuestionsAndAnswersSmartApplyApplicationState | null {
    try {
        // Use cache.identify() for proper cache key generation
        const cacheId = cache.identify({
            __typename: 'SmartApplyApplicationState',
            id,
        });

        if (!cacheId) {
            console.warn(`⚠️  Failed to identify SmartApplyApplicationState with id: ${id}`);
            return null;
        }

        let updatedQuestionsAndAnswers: ScreenerQuestionsAndAnswersSmartApplyApplicationState | null =
            null;

        const modifyResult = cache.modify({
            id: cacheId,
            fields: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                questionsAndAnswers: (existingQuestionsAndAnswers: any) => {
                    // Ensure we have a complete base object
                    const baseQuestionsAndAnswers: ScreenerQuestionsAndAnswersSmartApplyApplicationState =
                        existingQuestionsAndAnswers || {
                            __typename: 'ScreenerQuestionsAndAnswersSmartApplyApplicationState',
                            _id: id,
                            screener: [],
                        };

                    // ✅ Merge updates into complete object (all fields preserved)
                    const newQuestionsAndAnswers: ScreenerQuestionsAndAnswersSmartApplyApplicationState =
                        {
                            ...baseQuestionsAndAnswers,
                            ...questionsUpdates, // Only override specified fields
                        };

                    updatedQuestionsAndAnswers = newQuestionsAndAnswers;
                    return newQuestionsAndAnswers;
                },
            },
        });

        if (!modifyResult) {
            console.warn(
                `⚠️  Cache modify returned false for ${id} - entity may not exist in cache`,
            );
            return null;
        }

        console.log(
            `✅ Updated SmartApplyApplicationState(${id}) questionsAndAnswers in cache:`,
            questionsUpdates,
        );
        return updatedQuestionsAndAnswers;
    } catch (error) {
        console.error(
            '❌ Failed to update SmartApplyApplicationState questionsAndAnswers cache:',
            error,
        );
        return null;
    }
}

/**
 * ✅ SAFE: Update a single question answer in smartApplyApplicationState cache
 *
 * This function only updates the Apollo cache for a specific question answer.
 * Use this for immediate UI updates when a user answers a single question.
 *
 * @param cache - Apollo cache instance
 * @param id - SmartApplyApplicationState ID
 * @param questionId - Specific question ID to update
 * @param answerValue - New answer value
 * @returns Updated questionsAndAnswers or null if operation failed
 */
export function updateSmartApplyQuestionCache(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: ApolloCache<any>,
    id: string,
    questionId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    answerValue: any,
): ScreenerQuestionsAndAnswersSmartApplyApplicationState | null {
    try {
        const cacheId = cache.identify({
            __typename: 'SmartApplyApplicationState',
            id,
        });

        if (!cacheId) {
            console.warn(`⚠️  Failed to identify SmartApplyApplicationState with id: ${id}`);
            return null;
        }

        let updatedQuestionsAndAnswers: ScreenerQuestionsAndAnswersSmartApplyApplicationState | null =
            null;

        const modifyResult = cache.modify({
            id: cacheId,
            fields: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                questionsAndAnswers: (existingQuestionsAndAnswers: any) => {
                    // Ensure we have a complete base object
                    const baseQuestionsAndAnswers: ScreenerQuestionsAndAnswersSmartApplyApplicationState =
                        existingQuestionsAndAnswers || {
                            __typename: 'ScreenerQuestionsAndAnswersSmartApplyApplicationState',
                            _id: id,
                            screener: [],
                        };

                    // Find and update the specific question
                    const updatedScreener =
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        baseQuestionsAndAnswers.screener?.map((screenerItem: any) => {
                            if (screenerItem?.questionId === questionId) {
                                return {
                                    ...screenerItem,
                                    answer: {
                                        __typename: 'TextAnswerSmartApplyApplicationState', // Adjust based on answer type
                                        value: answerValue,
                                    },
                                };
                            }
                            return screenerItem;
                        }) || [];

                    const newQuestionsAndAnswers: ScreenerQuestionsAndAnswersSmartApplyApplicationState =
                        {
                            ...baseQuestionsAndAnswers,
                            screener: updatedScreener,
                        };

                    updatedQuestionsAndAnswers = newQuestionsAndAnswers;
                    return newQuestionsAndAnswers;
                },
            },
        });

        if (!modifyResult) {
            console.warn(
                `⚠️  Cache modify returned false for ${id} - entity may not exist in cache`,
            );
            return null;
        }

        console.log(
            `✅ Updated SmartApplyApplicationState(${id}) question ${questionId} in cache:`,
            answerValue,
        );
        return updatedQuestionsAndAnswers;
    } catch (error) {
        console.error('❌ Failed to update SmartApplyApplicationState question cache:', error);
        return null;
    }
}

/**
 * ✅ SAFE: Update smartApplyApplicationState resume with complete data preservation
 *
 * This function only updates the Apollo cache - it does NOT call server mutations.
 * Use this for immediate UI updates, then call buildCompleteResumeMutation()
 * when you need to save to the server.
 *
 * @param cache - Apollo cache instance
 * @param id - SmartApplyApplicationState ID
 * @param resumeUpdates - Partial resume updates
 * @returns Updated resume or null if operation failed
 */
export function updateSmartApplyResumeCache(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: ApolloCache<any>,
    id: string,
    resumeUpdates: SmartApplyResumeUpdate,
): ResumeSmartApplyApplicationState | null {
    try {
        // Use cache.identify() for proper cache key generation
        const cacheId = cache.identify({
            __typename: 'SmartApplyApplicationState',
            id,
        });

        if (!cacheId) {
            console.warn(`⚠️  Failed to identify SmartApplyApplicationState with id: ${id}`);
            return null;
        }

        let updatedResume: ResumeSmartApplyApplicationState | null = null;

        const modifyResult = cache.modify({
            id: cacheId,
            fields: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                resume: (existingResume: any) => {
                    // Ensure we have a complete base object
                    const baseResume: ResumeSmartApplyApplicationState = existingResume || {
                        __typename: 'ResumeSmartApplyApplicationState',
                        resumeDetail: null,
                        tailoredResume: null,
                    };

                    // ✅ Merge updates into complete object (all fields preserved)
                    const newResume: ResumeSmartApplyApplicationState = {
                        ...baseResume,
                        ...resumeUpdates, // Only override specified fields
                    };

                    updatedResume = newResume;
                    return newResume;
                },
            },
        });

        if (!modifyResult) {
            console.warn(
                `⚠️  Cache modify returned false for ${id} - entity may not exist in cache`,
            );
            return null;
        }

        console.log(`✅ Updated SmartApplyApplicationState(${id}) resume in cache:`, resumeUpdates);
        return updatedResume;
    } catch (error) {
        console.error('❌ Failed to update SmartApplyApplicationState resume cache:', error);
        return null;
    }
}

/**
 * ✅ SAFE: Get complete SmartApplyApplicationState from cache
 *
 * Use this to get the complete current state before building mutation inputs.
 * This ensures you have all fields needed for safe server mutations.
 *
 * @param cache - Apollo cache instance
 * @param id - SmartApplyApplicationState ID
 * @returns Complete SmartApplyApplicationState or null if not found
 */
export function getCompleteSmartApplyStateFromCache(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: ApolloCache<any>,
    id: string,
): SmartApplyApplicationState | null {
    try {
        const cacheId = cache.identify({
            __typename: 'SmartApplyApplicationState',
            id,
        });

        if (!cacheId) {
            console.warn(`⚠️  Failed to identify SmartApplyApplicationState with id: ${id}`);
            return null;
        }

        // Read the complete object from cache
        const cachedState = cache.readFragment({
            id: cacheId,
            fragment: gql`
                fragment CachedSmartApplyState on SmartApplyApplicationState {
                    __typename
                    id
                    draftId
                    contactInfo {
                        __typename
                        firstName
                        lastName
                        firstNamePronunciation
                        lastNamePronunciation
                        email
                        phoneNumber
                        dateOfBirth
                        gender
                        area
                        city
                        country
                        resumeContactInfo {
                            __typename
                            name {
                                __typename
                                firstName
                                lastName
                            }
                            phone
                            location {
                                __typename
                                city
                                country
                            }
                        }
                    }
                    resume {
                        __typename
                        resumeDetail {
                            __typename
                            origin
                            files {
                                __typename
                                type
                                uri
                            }
                        }
                        tailoredResume {
                            __typename
                            firstName
                            lastName
                            phoneNumber
                            location {
                                __typename
                                postalCode
                                unknownLocation
                            }
                            workExperiences {
                                __typename
                                title
                                company
                                description
                            }
                            educations {
                                __typename
                                degree {
                                    __typename
                                    degreeName
                                    field
                                }
                                school
                            }
                            skills {
                                __typename
                                experience
                            }
                        }
                    }
                    questionsAndAnswers {
                        __typename
                        screener {
                            __typename
                            questionId
                            answer {
                                __typename
                                ... on TextAnswerSmartApplyApplicationState {
                                    value
                                }
                            }
                        }
                    }
                }
            `,
        });

        return cachedState as SmartApplyApplicationState;
    } catch (error) {
        console.error('❌ Failed to read complete SmartApplyApplicationState from cache:', error);
        return null;
    }
}

/**
 * ✅ SAFE: Build complete contactInfo mutation input with data preservation
 *
 * This function ensures mutations never lose data by including ALL contactInfo fields.
 * Use this when preparing mutations to send to the server.
 *
 * @param currentContactInfo - Complete current contactInfo from cache/query (flexible type)
 * @param updates - Partial updates to apply
 * @returns Complete mutation input with all fields (existing + updates)
 */
export function buildCompleteContactInfoMutationInput(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentContactInfo: any, // Flexible type to accept GraphQL query results
    updates: SmartApplyContactInfoUpdate,
): UpdateContactInfoSmartApplyApplicationStateInput {
    // Start with complete base object (never send partial data to server)
    const baseContactInfo = currentContactInfo || {
        firstName: null,
        lastName: null,
        firstNamePronunciation: null,
        lastNamePronunciation: null,
        email: null,
        phoneNumber: null,
        dateOfBirth: null,
        gender: null,
        area: null,
        city: null,
        country: null,
    };

    // ✅ Build complete mutation input (ALL fields included to prevent data loss)
    const completeMutationInput: UpdateContactInfoSmartApplyApplicationStateInput = {
        // Core name fields - always include to prevent loss
        firstName: updates.firstName !== undefined ? updates.firstName : baseContactInfo.firstName,
        lastName: updates.lastName !== undefined ? updates.lastName : baseContactInfo.lastName,

        // Name pronunciation fields - always include to prevent loss
        firstNamePronunciation:
            updates.firstNamePronunciation !== undefined
                ? updates.firstNamePronunciation
                : baseContactInfo.firstNamePronunciation,
        lastNamePronunciation:
            updates.lastNamePronunciation !== undefined
                ? updates.lastNamePronunciation
                : baseContactInfo.lastNamePronunciation,

        // Contact fields - always include to prevent loss
        email: updates.email !== undefined ? updates.email : baseContactInfo.email,
        phoneNumber:
            updates.phoneNumber !== undefined ? updates.phoneNumber : baseContactInfo.phoneNumber,

        // Personal info fields - always include to prevent loss
        dateOfBirth:
            updates.dateOfBirth !== undefined ? updates.dateOfBirth : baseContactInfo.dateOfBirth,
        gender: updates.gender !== undefined ? updates.gender : baseContactInfo.gender,

        // Location fields - always include to prevent loss
        area: updates.area !== undefined ? updates.area : baseContactInfo.area,
        city: updates.city !== undefined ? updates.city : baseContactInfo.city,
        country: updates.country !== undefined ? updates.country : baseContactInfo.country,

        // Additional mutation-specific fields
        shouldUpdateProfile: true, // Standard behavior for contact info updates
    };

    console.log('✅ Built complete contactInfo mutation input (ALL fields included):', {
        updateCount: Object.keys(updates).length,
        totalFields: Object.keys(completeMutationInput).length - 1, // -1 for shouldUpdateProfile
        updates: updates,
    });

    return completeMutationInput;
}

/**
 * ✅ SAFE: Build complete questionsAndAnswers mutation input with data preservation
 *
 * This function ensures mutations never lose data by including ALL questionsAndAnswers fields.
 * Use this when preparing questions mutations to send to the server.
 *
 * @param currentQuestionsAndAnswers - Complete current questionsAndAnswers from cache/query (flexible type)
 * @param updates - Partial updates to apply
 * @returns Complete mutation input with all fields (existing + updates)
 */
export function buildCompleteQuestionsAndAnswersMutationInput(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentQuestionsAndAnswers: any, // Flexible type to accept GraphQL query results
    updates: SmartApplyQuestionsUpdate,
): UpdateQuestionsAndAnswersSmartApplyApplicationStateInput {
    // Start with complete base object (never send partial data to server)
    const baseQuestionsAndAnswers = currentQuestionsAndAnswers || {
        screener: [],
    };

    // ✅ Build complete mutation input (ALL fields included to prevent data loss)
    const completeMutationInput: UpdateQuestionsAndAnswersSmartApplyApplicationStateInput = {
        // ApplicationDraftId is required for the mutation
        applicationDraftId:
            baseQuestionsAndAnswers._id || baseQuestionsAndAnswers.applicationDraftId || '',
        // Always include complete screener array
        screener:
            updates.screener !== undefined ? updates.screener : baseQuestionsAndAnswers.screener,
    };

    console.log('✅ Built complete questionsAndAnswers mutation input (ALL fields included):', {
        updateCount: Object.keys(updates).length,
        totalFields: Object.keys(completeMutationInput).length,
        updates: updates,
    });

    return completeMutationInput;
}

/**
 * ✅ SAFE: Build complete resume mutation input with data preservation
 *
 * This function ensures mutations never lose data by including ALL resume fields.
 * Use this when preparing resume mutations to send to the server.
 *
 * @param currentResume - Complete current resume from cache/query (flexible type)
 * @param updates - Partial updates to apply
 * @returns Complete mutation input with all fields (existing + updates)
 */
export function buildCompleteResumeMutationInput(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentResume: any, // Flexible type to accept GraphQL query results
    updates: SmartApplyResumeUpdate,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
    // Return type will depend on the specific mutation schema
    // Start with complete base object (never send partial data to server)
    const baseResume = currentResume || {
        resumeDetail: null,
        tailoredResume: null,
    };

    // ✅ Build complete mutation input (ALL fields included to prevent data loss)
    const completeMutationInput = {
        // Always include complete resume structure
        resumeDetail:
            updates.resumeDetail !== undefined ? updates.resumeDetail : baseResume.resumeDetail,
        tailoredResume:
            updates.tailoredResume !== undefined
                ? updates.tailoredResume
                : baseResume.tailoredResume,
        // Add other resume fields as they become available
    };

    console.log('✅ Built complete resume mutation input (ALL fields included):', {
        updateCount: Object.keys(updates).length,
        totalFields: Object.keys(completeMutationInput).length,
        updates: updates,
    });

    return completeMutationInput;
}

/**
 * ✅ UTILITY: Validate cache-server consistency after mutations
 *
 * Use this in mutation update functions to verify the server returned
 * the same data that was sent, catching any unexpected server behavior.
 *
 * @param sentData - Data that was sent to the server
 * @param receivedData - Data received back from the server
 * @param operationName - Name of operation for logging
 * @throws Error if inconsistencies are detected
 */
export function validateCacheServerConsistency(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sentData: Record<string, any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    receivedData: Record<string, any>,
    operationName: string,
): void {
    // Control fields that are mutation directives, not data fields
    // These are sent to the server but not returned in the response
    const controlFields = new Set([
        'shouldUpdateProfile',
        'applicationDraftId',
        // Add other control fields as needed
    ]);

    // Server-constrained fields that may be rejected due to business rules
    // These fields may be sent but returned as null due to server validation
    const serverConstrainedFields = new Set([
        'area', // May be rejected due to location validation constraints
        // Add other fields that have server-side validation constraints
    ]);

    const inconsistencies: string[] = [];

    // Check each field that was sent, excluding control fields
    Object.entries(sentData).forEach(([field, expectedValue]) => {
        // Skip control fields - they're directives to the server, not data to be returned
        if (controlFields.has(field)) {
            console.log(`⚪ Skipping control field validation: ${field} (mutation directive only)`);
            return;
        }

        const actualValue = receivedData[field];
        if (actualValue !== expectedValue) {
            if (serverConstrainedFields.has(field)) {
                console.warn(
                    `⚠️  Server rejected field due to business rules: ${field}: expected "${expectedValue}", got "${actualValue}"`,
                );
                console.warn(
                    `📝 This is expected behavior - server validation prevented the update`,
                );
            } else {
                inconsistencies.push(`${field}: expected "${expectedValue}", got "${actualValue}"`);
            }
        }
    });

    if (inconsistencies.length > 0) {
        const errorMessage = `❌ Cache-server inconsistency in ${operationName}: ${inconsistencies.join(', ')}`;
        console.error(errorMessage, { sentData, receivedData });
        throw new Error(errorMessage);
    }

    const totalFields = Object.keys(sentData).length;
    const controlFieldsInSent = Object.keys(sentData).filter((field) =>
        controlFields.has(field),
    ).length;
    const dataFieldsValidated = totalFields - controlFieldsInSent;

    console.log(
        `✅ Cache and server state are consistent for ${operationName} (${dataFieldsValidated} data fields validated)`,
    );
}

// GraphQL import for fragments (required for readFragment)
import { gql } from '@apollo/client';

/**
 * ✅ SAFE: Update jobSeekerProfile location with cache preservation
 *
 * This function updates JobSeekerProfile location fields in Apollo cache.
 * Used for address fields that aren't part of SmartApplyApplicationState.
 *
 * @param cache - Apollo cache instance
 * @param locationUpdate - Partial location updates (postalCode, address, address2, etc.)
 * @returns Updated location or null if operation failed
 */
export function updateJobSeekerProfileLocationCache(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: ApolloCache<any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    locationUpdate: Record<string, any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
    try {
        // Method 1: Target the actual normalized cache entry (JobSeekerProfile:{})
        let success = false;

        console.log('🔍 Attempting to update normalized JobSeekerProfile cache entry...');

        cache.modify({
            id: 'JobSeekerProfile:{}', // Target the actual referenced object
            fields: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                defaultInfo(existingDefaultInfo: any) {
                    if (!existingDefaultInfo) {
                        console.warn('⚠️  No existing defaultInfo found in JobSeekerProfile cache');
                        return existingDefaultInfo;
                    }

                    console.log('🔍 Current defaultInfo structure:', existingDefaultInfo);

                    const updatedDefaultInfo = {
                        ...existingDefaultInfo,
                        contactInformation: {
                            ...existingDefaultInfo.contactInformation,
                            location: {
                                __typename: 'JobSeekerProfileLocationType',
                                ...existingDefaultInfo.contactInformation?.location,
                                ...locationUpdate, // Apply the location updates
                            },
                        },
                    };

                    success = true;
                    console.log(
                        '✅ Updated JobSeekerProfile defaultInfo location via normalized cache:',
                        locationUpdate,
                    );
                    return updatedDefaultInfo;
                },
            },
        });

        // Method 1b: Also try updating at the contactInformation level for double coverage
        if (success) {
            cache.modify({
                id:
                    cache.identify({
                        __typename: 'JobSeekerProfileContactInformation',
                    }) || 'JobSeekerProfileContactInformation:{}',
                fields: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    location(existingLocation: any) {
                        console.log(
                            '🔍 Updating contactInformation location directly:',
                            existingLocation,
                        );
                        return {
                            __typename: 'JobSeekerProfileLocationType',
                            ...existingLocation,
                            ...locationUpdate,
                        };
                    },
                },
            });
        }

        // Method 2: Try writeFragment approach for normalized cache
        if (!success) {
            console.log('⚠️  Trying writeFragment approach for normalized cache...');
            try {
                // Try to find and update the location fragment directly
                cache.writeFragment({
                    id: cache.identify({
                        __typename: 'JobSeekerProfileLocationType',
                    }),
                    fragment: gql`
                        fragment LocationUpdate on JobSeekerProfileLocationType {
                            postalCode
                            address
                            address2
                            country
                        }
                    `,
                    data: {
                        __typename: 'JobSeekerProfileLocationType',
                        postalCode: locationUpdate.postalCode || '',
                        address: locationUpdate.address || '',
                        address2: locationUpdate.address2 || '',
                        country: locationUpdate.country || 'JP',
                    },
                });
                console.log('✅ Updated location via writeFragment:', locationUpdate);
                success = true;
            } catch (fragmentError) {
                console.warn('⚠️  WriteFragment approach failed:', fragmentError);
            }
        }

        // Method 3: If writeFragment didn't work, try writeQuery as fallback
        if (!success) {
            console.log('⚠️  Trying writeQuery fallback...');

            // Try to read the current query data and write it back with updates
            try {
                const currentData = cache.readQuery({
                    query: gql`
                        query GetJobSeekerProfileForCache {
                            jobSeekerProfile {
                                profile {
                                    defaultInfo {
                                        contactInformation {
                                            location {
                                                postalCode
                                                address
                                                address2
                                                country
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                });

                if (
                    currentData &&
                    typeof currentData === 'object' &&
                    'jobSeekerProfile' in currentData &&
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (currentData as any).jobSeekerProfile
                ) {
                    const updatedData = {
                        ...currentData,
                        jobSeekerProfile: {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ...(currentData as any).jobSeekerProfile,
                            profile: {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                ...(currentData as any).jobSeekerProfile.profile,
                                defaultInfo: {
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    ...(currentData as any).jobSeekerProfile.profile?.defaultInfo,
                                    contactInformation: {
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        ...(currentData as any).jobSeekerProfile.profile
                                            ?.defaultInfo?.contactInformation,
                                        location: {
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            ...(currentData as any).jobSeekerProfile.profile
                                                ?.defaultInfo?.contactInformation?.location,
                                            ...locationUpdate,
                                        },
                                    },
                                },
                            },
                        },
                    };

                    cache.writeQuery({
                        query: gql`
                            query GetJobSeekerProfileForCache {
                                jobSeekerProfile {
                                    profile {
                                        defaultInfo {
                                            contactInformation {
                                                location {
                                                    postalCode
                                                    address
                                                    address2
                                                    country
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        `,
                        data: updatedData,
                    });

                    console.log(
                        '✅ Updated JobSeekerProfile location using writeQuery:',
                        locationUpdate,
                    );
                    return locationUpdate;
                }
            } catch (writeError) {
                console.warn('⚠️  WriteQuery approach also failed:', writeError);
            }
        }

        // Method 4: Last resort - evict and let refetch handle it
        if (!success) {
            console.log('⚠️  All direct cache methods failed, trying cache eviction...');
            try {
                // Evict the jobSeekerProfile field to force a refetch
                cache.evict({
                    id: 'ROOT_QUERY',
                    fieldName: 'jobSeekerProfile',
                });
                console.log('✅ Evicted jobSeekerProfile from cache - will refetch on next access');
                // Note: This will cause a refetch, but at least the user will see updated data
                success = true;
            } catch (evictError) {
                console.warn('⚠️  Even cache eviction failed:', evictError);
            }
        }

        // Method 5: Try updating the cache with variables (most direct approach)
        if (!success) {
            console.log('⚠️  Trying direct cache write with query variables...');
            try {
                // For this to work, we'd need the nodeId and indeedApplyableJobId
                // For now, let's just log that we tried this approach
                console.log(
                    '📝 Would need query variables to write directly to GetAddressFormData cache',
                );
            } catch (directError) {
                console.warn('⚠️  Direct cache write also failed:', directError);
            }
        }

        return success ? locationUpdate : null;
    } catch (error) {
        console.error('❌ Failed to update JobSeekerProfile location cache:', error);
        console.error('Error details:', error);
        return null;
    }
}
